import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import { ClickAwayListener } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { TYPOGRAPHY } from '../../design-system/settings/variables/Typography';
import { thinScrollbarStyles } from '../../design-system/settings/variables/Scrollbar';

const StyledDiv = styled.div<{
  minWidth?: string;
  disabled?: boolean;
  borderColor?: keyof typeof COLORS;
}>`
  position: relative;
  ${({ disabled }) =>
    disabled
      ? `* {cursor: not-allowed !important; color: ${COLORS.grayBase}}`
      : ''}
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth};` : '')}
  ${({ borderColor }) =>
    borderColor
      ? `border-radius: 4px; border: 1px solid ${COLORS[borderColor]};`
      : ''}
  .filter_dropdown__wrapper {
    ${thinScrollbarStyles}
    box-shadow: 0px 20px 36px rgba(0, 0, 0, 0.15);
    border-radius: 0 0 4px 4px;
    position: absolute;
    left: 0;
    top: 48px;
    z-index: 1;
    width: 100%;
    max-height: 453px;
    overflow-y: auto;
    opacity: 0;
    ${({ minWidth }) => (minWidth ? `min-width: ${minWidth};` : '')}
    background-color: ${COLORS.grayLightest};
    color: ${COLORS.blackBase};
    // padding: ${SPACERS.small};
    > * {
      padding: ${SPACERS.small};
    }
    transition: all 0.15s;
    &.open {
      opacity: 1;
      transition: all 0.15s;
    }
    .search__wrapper {
      // margin-bottom: ${SPACERS.tiny};
      padding: ${SPACERS.small} ${SPACERS.small} 0 ${SPACERS.small};
      .search-input {
        border: none;
        outline: none;
        width: 100%;
      }
    }
    .radio-button {
      padding-top: 11px;
      padding-bottom: 11px;
      padding-left: 12px;
      cursor: pointer;
      transition: all 0.1s ease-in;
      &:hover {
        color: ${COLORS.whiteBase}
        background-color: ${COLORS.grayDark};
      }
      .fa-check {
        padding-right: 8px;
      }
    }
    .checkbox__wrapper {
      display: flex;
      align-items: center;
      .checkbox__count {
        margin-left: auto;
        font-weight: ${TYPOGRAPHY.fontWeightBase};
      }
    }
    .cta_clear {
      position: sticky;
      bottom: 0;
      left: 0;
      padding: ${SPACERS.small};
      background-color: ${COLORS.whiteBase};
      width: 100%;
      font-weight: ${TYPOGRAPHY.fontWeightBase};
      text-align: center;
      color: ${COLORS.ctaPrimary};
      cursor: pointer;
      transition: color 0.5s;
    }
    .disable-clear-button {
      cursor: default;
      color: ${COLORS.greyBase};
    }
  }
  .filter_select {
    cursor: pointer;
    background-color: ${COLORS.whiteBase};
    color: ${COLORS.blackBase};
    padding: ${SPACERS.tiny} ${SPACERS.small};
    font-weight: ${TYPOGRAPHY.fontWeightBold};
    border-radius: 4px;
    display: flex;
    flex-grow: 1;
    align-items: center;
    gap: ${SPACERS.small};
    &.open {
      border-radius: 4px 4px 0 0;
    }
    .label_group {
      display: flex;
      align-items: center;
      .label_icon {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .selector_label {
        font-size: 0.875rem;
      }
      svg {
        color: ${COLORS.ctaPrimary};
        margin-right: 4px;
      }
    }
    .cta_icon {
      margin-left: auto;
    }
  }
`;

interface FeedbackDashboardFilterSelectProps {
  selectorLabel: JSX.Element | string | number;
  native?: boolean;
  borderColor?: keyof typeof COLORS;
  icon: IconProp | IconDefinition | undefined;
  clearButtonText?: string;
  disabled?: boolean;
  disableClearButton: boolean;
  minWidth?: string;
  className?: string;
}

type FilterDropdown =
  | {
      type: 'select';
      children: React.ReactNode;
      Component: undefined;
      onClose: () => unknown;
      onClearAll: (onClose: () => unknown) => unknown;
    }
  | {
      type: 'radio';
      Component: (handleClose: () => unknown) => React.ReactNode;
      onClose: undefined;
      onClearAll: (() => unknown) | undefined;
    };

const FeedbackDashboardFilterSelect: React.FC<FeedbackDashboardFilterSelectProps &
  FilterDropdown> = ({
  selectorLabel,
  borderColor,
  minWidth,
  disabled,
  native = false,
  icon,
  onClose,
  onClearAll,
  clearButtonText = 'Clear All',
  disableClearButton,
  className,
  type,
  Component,
  children,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleClick = useCallback(() => {
    setOpen(o => {
      o && onClose?.();
      return !o;
    });
  }, [setOpen, onClose]);

  const handleClose = useCallback(() => {
    onClose?.();
    setOpen(false);
  }, [setOpen, onClose]);

  const handleClearAll = useCallback(() => {
    onClearAll?.(handleClose);
  }, [onClearAll, handleClose]);

  if (native) {
    return <>{type === 'select' ? children : Component(handleClose)}</>;
  }

  return (
    <StyledDiv
      borderColor={borderColor}
      minWidth={minWidth}
      disabled={disabled}
      className={className}
    >
      <div
        onClick={disabled ? undefined : handleClick}
        className={`filter_select ${open ? 'open' : ''}`}
      >
        <div className="label_group">
          {icon && (
            <div className="label_icon">
              <FontAwesomeIcon icon={icon} />
            </div>
          )}
          <TrueImpactText
            allowTextSelection={false}
            variant="body-bold"
            className="selector_label"
          >
            {selectorLabel}
          </TrueImpactText>
        </div>
        <div className="cta_icon">
          {open ? (
            <FontAwesomeIcon icon={faAngleUp} />
          ) : (
            <FontAwesomeIcon icon={faAngleDown} />
          )}
        </div>
      </div>
      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <div className="filter_dropdown__wrapper open">
            {type === 'select' ? children : Component(handleClose)}
            {onClearAll && (
              <TrueImpactText
                allowTextSelection={false}
                onClick={handleClearAll}
                variant="body-small"
                className={`cta_clear${
                  disableClearButton ? ' disable-clear-button' : ''
                }`}
              >
                {clearButtonText}
              </TrueImpactText>
            )}
          </div>
        </ClickAwayListener>
      )}
    </StyledDiv>
  );
};

export default FeedbackDashboardFilterSelect;
